import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { TextField } from "../shared/FormField";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { formatDate } from "../../lib/utils";

// Reusable Pause / Resume dialog for both admin (subscription profile) and the
// customer portal. Pass the surface-specific endpoints via props.
//   mode="pause"   -> date-range picker (From/To) + confirm step
//   mode="resume"  -> shows the current pause batch summary + a single Resume button
export function PauseResumeDialog({
  open, onOpenChange, mode,
  pauseUrl, resumeUrl, batchUrl,
  pauseMinDate, onDone,
}) {
  const isPause = mode === "pause";
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [batch, setBatch] = useState(null);
  const [loadingBatch, setLoadingBatch] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFromDate(""); setToDate(""); setConfirming(false); setBatch(null);
    if (!isPause && batchUrl) {
      setLoadingBatch(true);
      apiClient.get(batchUrl)
        .then(({ data }) => setBatch(data))
        .catch(() => setBatch(null))
        .finally(() => setLoadingBatch(false));
    }
  }, [open, isPause, batchUrl]);

  const handlePause = async () => {
    setSaving(true);
    try {
      await apiClient.post(pauseUrl, { from_date: fromDate, to_date: toDate });
      toast.success("Deliveries paused for the selected dates");
      onDone();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not pause this subscription."));
    } finally {
      setSaving(false);
    }
  };

  const handleResume = async () => {
    setSaving(true);
    try {
      await apiClient.post(resumeUrl);
      toast.success("Pause cancelled — deliveries will resume");
      onDone();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not resume this subscription."));
    } finally {
      setSaving(false);
    }
  };

  const canCancel = batch?.has_pause && batch.days_left > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="subscription-pause-resume-dialog">
        <DialogHeader>
          <DialogTitle>{isPause ? "Pause deliveries" : "Resume deliveries"}</DialogTitle>
          <DialogDescription>
            {isPause
              ? "Skip deliveries for a date range. The end date is pushed out to compensate — this does not deactivate the subscription."
              : "Cancel the scheduled pause. Deliveries will resume from the cutoff date onward; any days already passed stay paused."}
          </DialogDescription>
        </DialogHeader>

        {isPause ? (
          <>
            <div className="grid grid-cols-2 gap-4">
              <TextField label="From" required type="date" min={pauseMinDate} value={fromDate} onChange={(e) => setFromDate(e.target.value)} testId="subscription-pause-resume-from-date" />
              <TextField label="To" required type="date" min={fromDate || pauseMinDate} value={toDate} onChange={(e) => setToDate(e.target.value)} testId="subscription-pause-resume-to-date" />
            </div>
            {confirming && (
              <div className="mt-3 rounded-md border border-warning/30 bg-warning-bg px-3 py-2 text-sm text-warning" data-testid="subscription-pause-confirm-note">
                Pause deliveries from <strong>{formatDate(fromDate)}</strong> to <strong>{formatDate(toDate)}</strong>? Tap Confirm to apply.
              </div>
            )}
            <DialogFooter>
              <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="subscription-pause-resume-cancel-button">
                Cancel
              </Button>
              {confirming ? (
                <Button variant="accent" disabled={saving} onClick={handlePause} data-testid="subscription-pause-resume-confirm-button">
                  {saving ? "Working..." : "Confirm Pause"}
                </Button>
              ) : (
                <Button variant="accent" disabled={!fromDate || !toDate} onClick={() => setConfirming(true)} data-testid="subscription-pause-review-button">
                  Review
                </Button>
              )}
            </DialogFooter>
          </>
        ) : (
          <>
            {loadingBatch ? (
              <p className="py-4 text-sm text-text-muted">Loading pause details…</p>
            ) : batch?.has_pause ? (
              <div className="space-y-3" data-testid="subscription-resume-summary">
                <div className="grid grid-cols-2 gap-3">
                  <Stat label="Pause starts" value={formatDate(batch.from_date)} testId="resume-from" />
                  <Stat label="Pause ends" value={formatDate(batch.to_date)} testId="resume-to" />
                  <Stat label="Total pause days" value={batch.total_days} testId="resume-total" />
                  <Stat label="Days remaining" value={batch.days_left} testId="resume-left" />
                </div>
                <div className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-muted" data-testid="resume-cutoff-note">
                  {canCancel
                    ? <>Deliveries resume from <strong className="text-text-main">{formatDate(batch.cutoff)}</strong>. {batch.days_left < batch.total_days && "Pause days before this date stay as-is."}</>
                    : "This pause can no longer be cancelled — only future days can be resumed, and none are left."}
                </div>
              </div>
            ) : (
              <p className="py-4 text-sm text-text-muted" data-testid="subscription-resume-empty">There's no active or upcoming pause to cancel.</p>
            )}
            <DialogFooter>
              <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="subscription-pause-resume-cancel-button">
                Close
              </Button>
              <Button variant="default" disabled={saving || !canCancel} onClick={handleResume} data-testid="subscription-pause-resume-confirm-button">
                {saving ? "Working..." : "Resume Deliveries"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-md border border-border bg-background p-3" data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-text-muted">{label}</p>
      <p className="mt-1 text-sm font-semibold text-text-main">{value}</p>
    </div>
  );
}
